import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import MembersContext from 'router/subrouters/DataRoom/providers/Members/Members.context';
import { Button } from 'ncoded-component-library';
import { DataRoomShareableLinkRequest } from 'models/ShareableLink';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import { getMixpanelErrorProps } from 'axiosUtils';
import { ErrorViewedProps } from 'models/MixpanelEvents';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import ResolvePopup from 'components/ResolvePopup';
import InformationcircleIcon from 'icons/Informationcircle.icon';
import WaitingListTableWrapper from '../WaitingListTableWrapper';
import XIcon from 'icons/X.icon';
import CreateNewSecureLinkButton from '../CreateNewSecureLinkButton';
import CheckIcon from 'icons/Check.icon';

import './WaitingListTable.styles.scss';
import './WaitingListTable.styles.responsive.scss';

type WaitingListTableProps = {
  className?: string;
};

const WaitingListTable: React.FC<WaitingListTableProps> = (props) => {
  const { className } = props;

  const classes = classNames('waiting-list-table', className);

  const { doesntHaveAccess } = useContext(DataRoomContext);
  const { waitingRoomItems, removeWaitingRoomItem } =
    useContext(MembersContext);
  const { popSuccess } = useContext(PopNotificationsContext);

  const { t } = useTranslation();

  const getMixpanelProps = useGetMixpanelProps();

  const onRejectAccess = useCallback(
    async (requestId: string) => {
      try {
        dataRoomApi.shareableLinks.rejectAccess(requestId);
        removeWaitingRoomItem(requestId);
        popSuccess(t('accessSuccesfullyRejected'));
      } catch (e) {
        mixpanelService.errorViewed(
          getMixpanelProps(getMixpanelErrorProps(e)) as ErrorViewedProps,
        );
      }
    },
    [getMixpanelProps, popSuccess, removeWaitingRoomItem, t],
  );

  const onGrantAccess = useCallback(
    (requestId: string) => {
      try {
        dataRoomApi.shareableLinks.grantAccess(requestId);
        removeWaitingRoomItem(requestId);
        popSuccess(t('accessSuccesfullyGranted'));
      } catch (e) {
        return;
      }
    },
    [popSuccess, removeWaitingRoomItem, t],
  );

  const renderButtons = useCallback(
    (item: DataRoomShareableLinkRequest) => {
      return (
        <>
          <ResolvePopup onHover popupContent={t('reject')}>
            <Button
              variant="icon"
              className="alert"
              icon={XIcon}
              disabled={doesntHaveAccess}
              onClick={() => {
                if (doesntHaveAccess) return;
                onRejectAccess(item._id);
              }}
            />
          </ResolvePopup>
          <CreateNewSecureLinkButton
            dataRoomShareableLink={item.dataRoomShareableLink}
            dataRoomShareableLinkRequestId={item._id}
            email={item.dataRoomShareableLinkConsumer.email}
          />
          <ResolvePopup onHover popupContent={t('grant')}>
            <Button
              variant="icon"
              className="success"
              icon={CheckIcon}
              disabled={doesntHaveAccess}
              onClick={() => {
                if (doesntHaveAccess) return;
                onGrantAccess(item._id);
              }}
            />
          </ResolvePopup>
        </>
      );
    },
    [doesntHaveAccess, onGrantAccess, onRejectAccess, t],
  );

  return (
    <WaitingListTableWrapper
      className={classes}
      items={waitingRoomItems}
      actionsColumn={
        <ResolvePopup onHover popupContent={t('moderateAccessPopup')}>
          {t('moderateAccess')}
          <InformationcircleIcon />
        </ResolvePopup>
      }
      getActions={renderButtons}
    />
  );
};

export default WaitingListTable;
